import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  Card,
  CardSet,
  Nullable,
  UserWithToken,
  WithData,
  WithMeta,
  MetaFilters,
  UpdateCardInput,
} from '@ygoargentina/sdk/api/api.types';

export const listGenesysCards = async (
  user: Nullable<UserWithToken>
) =>
  getGraphQLQueryClient(user).request<{ data: Card[] }>(
    gql`
      query {
        data: genesysCards {
          id
          name
          konamiId
          image
          frameType
          genesysPoints
          previousGenesysPoints
        }
      }
    `
  );

/** Un cambio de puntaje de Genesys ya registrado. */
export type GenesysPointChange = {
  id: number;
  cardId: number;
  points: number;
  previousPoints: number;
  listSlug: string;
  detectedAt: string;
};

/**
 * Historial completo del formato, del cambio más nuevo al más viejo.
 *
 * Todo junto y no por carta: son ~1000 filas para todo Genesys, y traerlo de una
 * permite que el panel de una carta se dibuje sin pedir nada. Pedirlo al abrir
 * dejaba un estado de carga, y el diálogo está centrado por transform: cualquier
 * cambio de alto lo re-centra y el modal salta a la vista.
 */
export const listGenesysHistory = async (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<{ data: GenesysPointChange[] }>(
    gql`
      query {
        data: genesysHistory {
          id
          cardId
          points
          previousPoints
          listSlug
          detectedAt
        }
      }
    `
  );

/**
 * Búsqueda de cartas contra el server, con lo justo para un selector.
 *
 * Existe aparte de `listCards` porque ese trae el catálogo entero con sus sets
 * (~22 MB con `limit: 0`) y no devuelve la imagen. Acá el filtrado lo hace la
 * base y viajan 20 filas.
 */
export const searchCards = async (
  user: Nullable<UserWithToken>,
  search: string,
  limit = 20,
  /**
   * `searchDescription` suma, después de las que matchean por nombre, las
   * cartas que nombran la búsqueda en su texto (las fusiones que piden a
   * "Fallen of Albaz").
   */
  options: { searchDescription?: boolean } = {}
) =>
  getGraphQLQueryClient(user).request<WithMeta<Card>>(
    gql`
      query searchCards($filters: CardFilters) {
        data: cards(filters: $filters) {
          data {
            id
            konamiId
            name
            image
            frameType
            archetype
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    {
      filters: {
        search,
        limit,
        ...(options.searchDescription && { searchDescription: true }),
      },
    }
  );

export const listCards = async (
  user: Nullable<UserWithToken>,
  filters?: Partial<MetaFilters>
) =>
  getGraphQLQueryClient(user).request<WithMeta<Card>>(
    gql`
      query ($filters: CardFilters) {
        data: cards(filters: $filters) {
          data {
            id
            name
            konamiId

            sets {
              id
              rarity
              label
              overframe
              alternateArt
              code

              set {
                konamiName
                code
                # Dos sets distintos comparten el mismo code cuando uno reimprime
                # al otro: la fecha separa las variantes homónimas.
                releaseDate
              }
            }

            attribute
            frameType
            type

            data {
              atk
            }
          }
        }
      }
    `,
    {
      filters,
    }
  );

/**
 * Varias cartas por `konamiId`, en un solo request.
 *
 * Para cuando hace falta el `type` de N cartas y nada más. Las alternativas no
 * servían: `getCard` es de a una y arrastra `description`, `image` y `sets`;
 * `searchByKonamiId` no devuelve `type` ni `frameType`; y `listCards` no filtra
 * por lista de ids, así que la única forma era traer el catálogo entero
 * (~14k cartas, ~22.8 MB).
 *
 * Los ids que no existen no vienen en la respuesta —el array puede ser más
 * corto que la lista pedida— y no hay orden garantizado: es un lookup por id.
 * El tope es de 200 ids por request; más que eso el API lo rechaza con
 * `too_many_konami_ids` en vez de recortar en silencio.
 *
 * Devuelve también el `neuronId` de cada carta, que es la traducción inversa a
 * `listCardsByNeuronIds`: el editor de mazos de la Konami Card Database
 * identifica las cartas por ese `cid` y no acepta el password, así que es lo
 * que hace falta para volcarle un `.ydk`.
 */
export const listCardsByKonamiIds = async (
  user: Nullable<UserWithToken>,
  konamiIds: number[]
) =>
  getGraphQLQueryClient(user).request<{
    data: (Card & { neuronId: number | string })[];
  }>(
    gql`
      query ($konamiIds: [Int!]!) {
        data: cardsByKonamiIds(konamiIds: $konamiIds) {
          id
          konamiId
          neuronId
          name
          type
          frameType
          image
        }
      }
    `,
    { konamiIds }
  );

/**
 * Para los ids de un `.ydk` que el catálogo no tiene, el passcode del catálogo
 * que les corresponde (artes alternativos, ids de pre-release de EDOPro). Sólo
 * vienen los que se pudieron traducir. Pública: la usa la app de decklist.
 */
export const listCanonicalKonamiIds = async (konamiIds: number[]) =>
  getGraphQLQueryClient(null).request<{
    data: { konamiId: number; canonical: number }[];
  }>(
    gql`
      query ($konamiIds: [Int!]!) {
        data: publicCanonicalKonamiIds(konamiIds: $konamiIds) {
          konamiId
          canonical
        }
      }
    `,
    { konamiIds }
  );

/** La impresión tal como la devuelve `cardSetsByCodes`: con su carta y su set. */
export type ScannedCardSet = Pick<
  CardSet,
  | 'id'
  | 'konamiId'
  | 'cardId'
  | 'code'
  | 'codes'
  | 'rarity'
  | 'label'
  | 'image'
  | 'lastCardMarket'
  | 'lastCardTrader'
  | 'lastTcgPlayer'
> & {
  setId: number;
  card: Pick<Card, 'id' | 'konamiId' | 'name' | 'image'>;
  set: { id: number; konamiName: string; code: string };
};

/**
 * Impresiones por código (`LEDE-EN045`). Es lo que usa el escáner de cartas:
 * los códigos salen de `extractCardScanText` (`@ygoargentina/helpers/card-codes`),
 * que ya los manda en la variante en inglés con la que están en el catálogo.
 *
 * Pública: no hace falta sesión. Un código puede devolver varias filas (otra
 * rareza, otro `label`) y los que no existen no vienen.
 */
export const listCardSetsByCodes = async (
  user: Nullable<UserWithToken>,
  codes: string[]
) =>
  getGraphQLQueryClient(user).request<{ data: ScannedCardSet[] }>(
    gql`
      query ($codes: [String!]!) {
        data: cardSetsByCodes(codes: $codes) {
          id
          konamiId
          setId
          cardId
          code
          codes {
            en
            sp
            fr
            de
            it
            pt
          }
          rarity
          label
          image
          lastCardMarket
          lastCardTrader
          lastTcgPlayer
          card {
            id
            konamiId
            name
            image
          }
          set {
            id
            konamiName
            code
          }
        }
      }
    `,
    { codes }
  );

/** `[konamiId, inglés, español?]` — ver `cardNameIndex` en el API. */
export type CardNameIndexRow = [number, string, string?];

/**
 * Nombres de todas las cartas, para el escaneo de mazos offline de la
 * super-app. ~14k filas (~800 KB): se baja una vez al activar la función y se
 * renueva sólo cuando cambia `getCardNameIndexVersion`. Pública.
 */
export const getCardNameIndex = async () =>
  getGraphQLQueryClient(null).request<{ data: { version: string; cards: CardNameIndexRow[] } }>(
    gql`
      query {
        data: cardNameIndex {
          version
          cards
        }
      }
    `
  );

/** Versión de `getCardNameIndex`, para saber si la copia local quedó vieja. */
export const getCardNameIndexVersion = async () =>
  getGraphQLQueryClient(null).request<{ data: string }>(
    gql`
      query {
        data: cardNameIndexVersion
      }
    `
  );

/**
 * Para el `onError` de una imagen de carta: devuelve la de alguna de sus
 * impresiones (o `null`) y el API encola la descarga de la imagen base, así
 * que la próxima carga ya la trae. No espera esa descarga.
 */
export const requestCardImage = async (
  user: Nullable<UserWithToken>,
  konamiId: number
) =>
  getGraphQLQueryClient(user).request<{ data: string | null }>(
    gql`
      mutation ($konamiId: Int!) {
        data: requestCardImage(konamiId: $konamiId)
      }
    `,
    { konamiId }
  );

/**
 * Varias cartas por `neuronId` — el id de 5 dígitos de la Konami Card Database.
 *
 * Es la traducción que hace falta para armar un `.ydk` con lo que entrega KCGN:
 * su decklist identifica las cartas por ese id y no expone el password de 8
 * dígitos en ningún campo, que es justamente de lo que está hecho un `.ydk`.
 *
 * Devuelve `neuronId` además de `konamiId` para poder aparear: los ids que no
 * existen no vienen en la respuesta, así que sin él no se sabe cuál faltó.
 * Mismo tope de 200 que `listCardsByKonamiIds`.
 */
export const listCardsByNeuronIds = async (
  user: Nullable<UserWithToken>,
  neuronIds: (number | string)[]
) =>
  getGraphQLQueryClient(user).request<{
    data: (Card & { neuronId: number | string })[];
  }>(
    gql`
      query ($neuronIds: [BigInt!]!) {
        data: cardsByNeuronIds(neuronIds: $neuronIds) {
          id
          konamiId
          neuronId
          name
          type
          frameType
        }
      }
    `,
    { neuronIds }
  );

/** Lo que entra en una ficha al pasar el mouse: qué es la carta y qué hace. */
export type CardPreview = Pick<
  Card,
  'id' | 'konamiId' | 'name' | 'description' | 'image' | 'type' | 'attribute'
> & {
  data: Card['data'];
};

/**
 * La carta, con lo justo para la ficha que se abre al pasar el mouse.
 *
 * Aparte de `getCard` porque esa arrastra `sets` entero —cada edición con su
 * rareza, su código y los seis idiomas de cada uno—, y una ficha que se abre
 * al pasar por encima se pide de a decenas mientras se recorre una grilla.
 * Misma query `card` del API, que es pública: la ficha aparece también en las
 * listas que mira un jugador sin cuenta.
 */
export const getCardPreview = async (
  user: Nullable<UserWithToken>,
  konamiId: number
) =>
  getGraphQLQueryClient(user).request<WithData<CardPreview>>(
    gql`
      query cardPreview($konamiId: Int!) {
        data: card(konamiId: $konamiId) {
          id
          konamiId
          name
          description
          image
          type
          attribute

          data {
            atk
            def
            level
            race
          }
        }
      }
    `,
    { konamiId }
  );

export const getCard = async (
  user: Nullable<UserWithToken>,
  variables: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<WithData<Card>>(
    gql`
      query ($konamiId: Int!) {
        data: card(konamiId: $konamiId) {
          id
          name
          description
          image
          konamiId
          konamiIdValidated
          neuronId
          completed
          imagesCompleted

          attribute
          archetype
          frameType
          type

          archetypes {
            id
            name
          }

          sets {
            id

            set {
              konamiName

              konamiNames {
                de
                en
                fr
                it
                sp
                pt
              }

              releaseDate
            }

            rarity
            label

            code
            codes {
              de
              en
              fr
              it
              sp
              pt
            }
          }

          names {
            de
            en
            fr
            it
            sp
            pt
          }

          descriptions {
            de
            en
            fr
            it
            sp
            pt
          }

          data {
            atk
            def
            level
            race
          }
        }
      }
    `,
    variables
  );

export const downloadImage = async (
  user: Nullable<UserWithToken>,
  variables: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<{ image: string }>(
    gql`
      query ($konamiId: Int!) {
        data: downloadImage(konamiId: $konamiId) {
          image
        }
      }
    `,
    variables
  );

/**
 * Sólo admin. Corrige metadata de la carta. Si cambia el `konamiId`, el API lo
 * arrastra a inventarios, mazos, precios, etc., y rechaza con
 * `konami_id_taken` si otra carta ya lo tiene.
 */
export const updateCard = async (
  user: Nullable<UserWithToken>,
  konamiId: number,
  input: UpdateCardInput
) =>
  getGraphQLQueryClient(user).request<WithData<Card>>(
    gql`
      mutation ($konamiId: Int!, $input: UpdateCardInput!) {
        data: updateCard(konamiId: $konamiId, input: $input) {
          id
          konamiId
        }
      }
    `,
    { konamiId, input }
  );

export const searchByKonamiId = async (
  user: Nullable<UserWithToken>,
  konamiId: number
) =>
  getGraphQLQueryClient(user).request<WithData<Card>>(
    gql`
      mutation ($konamiId: Int!) {
        data: searchByKonamiId(konamiId: $konamiId) {
          id
          name
          konamiId
        }
      }
    `,
    { konamiId }
  );

export const addCardToSet = async (
  user: Nullable<UserWithToken>,
  input: {
    cardId: number;
    konamiId: number;
    setId: number;
    name: string;
    rarity: string;
    code: string;
    label?: string;
    overframe?: boolean;
    alternateArt?: boolean;
  }
) =>
  getGraphQLQueryClient(user).request<WithData<Card>>(
    gql`
      mutation ($input: AddCardToSetInput!) {
        data: addCardToSet(input: $input) {
          id
          name
          konamiId
        }
      }
    `,
    { input }
  );

export const addCardsToSet = async (
  user: Nullable<UserWithToken>,
  input: {
    setId: number;
    cards: {
      cardId: number;
      konamiId: number;
      code: string;
      rarity: string;
      label?: string;
      overframe?: boolean;
      alternateArt?: boolean;
    }[];
  }
) =>
  getGraphQLQueryClient(user).request<
    WithData<{ created: number; skipped: number }>
  >(
    gql`
      mutation ($input: AddCardsToSetInput!) {
        data: addCardsToSet(input: $input) {
          created
          skipped
        }
      }
    `,
    { input }
  );

export const deleteCardSet = async (
  user: Nullable<UserWithToken>,
  cardSetId: number
) =>
  getGraphQLQueryClient(user).request<{ data: boolean }>(
    gql`
      mutation ($id: Int!) {
        data: deleteCardSet(id: $id)
      }
    `,
    { id: cardSetId }
  );

export type RefreshPriceResult = {
  success: boolean;
  refreshed: boolean;
  message: string | null;
  cardSet: {
    id: number;
    lastCardTrader: number | null;
  } | null;
};

export const refreshCardSetPrice = async (
  user: Nullable<UserWithToken>,
  cardSetId: number,
  force?: boolean
) =>
  getGraphQLQueryClient(user).request<{ data: RefreshPriceResult }>(
    gql`
      mutation ($cardSetId: Int!, $force: Boolean) {
        data: refreshCardSetPrice(cardSetId: $cardSetId, force: $force) {
          success
          refreshed
          message
          # lastCardTraderMin, lastCardTraderMax y lastPriceUpdate son
          # columnas de la tabla, no campos del tipo GraphQL: pedirlas
          # rechazaba la mutation entera (YGO-305).
          cardSet {
            id
            lastCardTrader
          }
        }
      }
    `,
    { cardSetId, force }
  );
